/**
 * useReservationChannelNotes — TanStack Query hook over
 * `GET /organizations/{slug}/reservations/{id}/channel-notes` (SCRUM-382).
 *
 * The DISPLAY side of operator-supplied Channel Reference Notes — the manual
 * counterpart to the channel-transcription `asReported`. Mirrors
 * `use-reservation-charges.ts` EXACTLY: the same five-branch status union
 * `loading | ready | forbidden | notFound | error`, the same Symbol-sentinel
 * posture, the same org-scoped queryKey.
 *
 * A 404 → `notFound` (honest-empty): the reservation has no channel reference
 * notes (a PropertyCollect/block booking, a never-frozen snapshot, or simply no
 * notes recorded), anti-enumeration-indistinguishable from cross-org RLS. The tab
 * renders an honest "no notes" empty state for that branch — NOT an error.
 *
 * Gated by `booking.reservations.read` (the SAME reservation-scoped read resource
 * as the detail — there is no `*.channel-notes.*` slug).
 */

import { useQuery } from '@tanstack/react-query';
import type { components } from '@artemis/contracts';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { bookingQueryKeys } from './queryKeys.js';

/** The generated channel-reference-notes body (provenance + nullable Cents + note). */
export type ChannelReferenceNotesBody = components['schemas']['ChannelReferenceNotesDto'];

/** 403 sentinel — the caller lacks `booking.reservations.read`. */
export const CHANNEL_NOTES_FORBIDDEN = Symbol('reservation-channel-notes:forbidden');

/** 404 sentinel — no notes recorded (anti-enum). A distinct, non-error empty state. */
export const CHANNEL_NOTES_NOT_FOUND = Symbol('reservation-channel-notes:notFound');

export type UseReservationChannelNotesResult =
  | { readonly status: 'loading'; readonly data?: undefined; readonly refetch: () => void }
  | { readonly status: 'ready'; readonly data: ChannelReferenceNotesBody; readonly refetch: () => void }
  | { readonly status: 'forbidden'; readonly data?: undefined; readonly refetch: () => void }
  | { readonly status: 'notFound'; readonly data?: undefined; readonly refetch: () => void }
  | { readonly status: 'error'; readonly data?: undefined; readonly refetch: () => void };

export function useReservationChannelNotes(
  reservationId: string | null,
): UseReservationChannelNotesResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;

  const query = useQuery({
    queryKey: [...bookingQueryKeys.channelNotes(reservationId ?? ''), orgSlug] as const,
    enabled: orgSlug !== null && reservationId != null,
    retry: false,
    queryFn: async () => {
      const result = await apiClient.GET(
        '/organizations/{slug}/reservations/{id}/channel-notes',
        {
          params: { path: { slug: orgSlug as string, id: reservationId as string } },
        },
      );
      if (result.response.status === 403) {
        return CHANNEL_NOTES_FORBIDDEN;
      }
      if (result.response.status === 404) {
        return CHANNEL_NOTES_NOT_FOUND;
      }
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `GET /organizations/${orgSlug}/reservations/${reservationId}/channel-notes failed with status ${result.response.status}`,
        );
      }
      return result.data;
    },
  });

  const refetch = (): void => {
    void query.refetch();
  };

  if (orgSlug === null || reservationId == null || query.isPending) {
    return { status: 'loading', refetch };
  }
  if (query.isError) {
    return { status: 'error', refetch };
  }
  if (query.data === CHANNEL_NOTES_FORBIDDEN) {
    return { status: 'forbidden', refetch };
  }
  if (query.data === CHANNEL_NOTES_NOT_FOUND) {
    return { status: 'notFound', refetch };
  }

  const envelope = query.data;
  return { status: 'ready', data: envelope.data, refetch };
}
